import { Dispatch } from '@reduxjs/toolkit';
import { produce } from 'immer';
import {
  get,
  initial,
  isEmpty,
  isMatch,
  isNaN,
  join,
  last,
  pick,
  set,
  snakeCase,
  split,
  startCase,
  trim,
} from 'lodash/fp';
import { customAlphabet } from 'nanoid';
import { change } from 'redux-form';

import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { INQUIRY_TEMPLATE_SCREEN_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { CombinedStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { findComponentConfigPath } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import {
  ComponentAccessTiers,
  RESTRICTED_COMPONENTS,
  ScreenEditorComponentConfigTypes,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import {
  ComponentSchemaValues,
  EDITOR_COMPONENTS_SCHEMA,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import {
  DraggableHoverPosition,
  EditorComponentSchema,
  ScreenEditorComponentItem,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { ComponentCopyData } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import {
  createFieldSchema,
  getComponentFieldKeys,
  getHumanReadableStepName,
  getNameForStep,
  getUpdatedComponentText,
  setTranslationDefaults,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import {
  isInitialStep,
  uiStepAllComponentConfigs,
  uiStepAllComponents,
  uiStepComponentConfigsRecursive,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import { clearedTransitionConfig } from 'dashboard/apps/InquiryTemplateEditor/utils/steps-update';
import { getProductUpsellLevel } from 'dashboard/components/ProductUpsellTooltip';
import { InquiryTemplateVersionStepUi } from 'dashboard/models/inquiry-template-version';
import { createMockCombinedStep } from 'inquiry/lib/stubs/inquiry';
import { FieldSchema, isFieldSchemaWithChoices } from 'lib/fields';
import { StepPreview, StepSelfieConfig, StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import {
  InputMultiFieldComponentFieldConfig,
  isImageComponentConfig,
  isImageHostedComponentConfig,
  isStylableUiParentComponentConfig,
  isTextComponent,
  isTextComponentConfig,
  isUiButtonComponentConfig,
  isUiFooterComponentConfig,
  isUiFormComponentConfig,
  isUiFormFieldOptionComponentConfig,
  isUiParentComponentConfig,
  isUiTransitionComponentConfig,
  TransitionConfigAttributes,
  UiComponent,
  UiComponentConfig,
  UiComponentConfigType,
  UiFormComponentConfig,
  UiFormMultiFieldComponentConfig,
  UiParentComponentConfig,
  UiTextComponentConfig,
  UiTransitionComponentConfig,
} from 'lib/interfaces/ui-components';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { createMockInquiry } from 'verify/lib/stubs/inquiry';
import { Inquiry } from 'verify/models/inquiry';

const nanoid = customAlphabet(
  'ABCDEFGHIGJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz01234567890',
  9,
);

const getBaseName = (itemConfig: UiComponentConfig) =>
  isUiButtonComponentConfig(itemConfig) ? 'button' : itemConfig.type;

export const componentNameRegex = (name: string) =>
  new RegExp(`^${name}-([0-9]*)(-[A-Za-z0-9-_]*)?$`);

type SelfiePendingLocalizationKey = keyof StepSelfieConfig['localizations']['pending-page'];
const selfiePendingLocalizationKeys: SelfiePendingLocalizationKey[] = ['title', 'description'];
export const getLabelStringForComponentName = (name: string, stepType: CombinedStepType) => {
  // Note (eric): Temporary and super hacky - atm clicking on the selfie capture preview also displays
  // the pending translation cards, as those are used within scanner. 'Pending' is prefixed to differentiate.
  if (stepType === UiStepType.Selfie && selfiePendingLocalizationKeys.includes(name as any)) {
    return `Pending ${startCase(name)} (Mobile SDK)`;
  }

  const captureId = name?.match(/[-]([a-zA-Z0-9]{9})\s*$/);
  if (captureId?.length) {
    return getHumanReadableStepName(name.replace(captureId[0], ''));
  }
  return getHumanReadableStepName(name);
};

export const generateRandomTypeName = (
  baseComponentName: UiComponentConfigType | string,
  step: InquiryTemplateVersionStepUi,
  additionalComponentConfigs: UiComponentConfig[] = [],
) => {
  const newNumberedConfigRegex = componentNameRegex(baseComponentName);
  const getMaxNum = (
    step: InquiryTemplateVersionStepUi,
    baseComponentName: UiComponentConfigType | string,
    additionalComponentConfigs: UiComponentConfig[],
  ): number =>
    uiStepAllComponentConfigs(step)
      .concat(uiStepComponentConfigsRecursive(additionalComponentConfigs))
      .filter((componentConfig) => getBaseName(componentConfig) === baseComponentName)
      .reduce((maxComponentNumber, componentConfig) => {
        const componentNumber = newNumberedConfigRegex.exec(componentConfig.name)?.[1] || '1';
        if (componentNumber && parseInt(componentNumber) > maxComponentNumber) {
          return parseInt(componentNumber);
        } else {
          return maxComponentNumber;
        }
      }, 0);

  const totalNumOfComponentType = getMaxNum(step, baseComponentName, additionalComponentConfigs);

  return totalNumOfComponentType > 0
    ? `${baseComponentName}-${totalNumOfComponentType + 1}-${nanoid()}`
    : `${baseComponentName}-${nanoid()}`;
};

export const createDedupedFieldKey = (
  fieldKeys: string[],
  fieldName: string,
  stepName?: string,
) => {
  return createDedupedKey({
    proposedKey: snakeCase(trim(fieldName)),
    currentKeys: fieldKeys,
    prefix: stepName && snakeCase(stepName),
  });
};

export const createDedupedKey = ({
  proposedKey,
  currentKeys,
  prefix,
}: {
  proposedKey: string;
  currentKeys: string[];
  prefix?: string;
}): string => {
  if (currentKeys.includes(proposedKey) || proposedKey.length === 0) {
    const splitFieldName = split('_', proposedKey);
    const suffix = last(splitFieldName);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const currentIndex = parseInt(suffix);
    if (isNaN(currentIndex)) {
      const name = prefix ? `${prefix}_${proposedKey}` : proposedKey;
      return currentKeys.includes(name)
        ? createDedupedKey({ proposedKey: `${name}_1`, currentKeys, prefix })
        : createDedupedKey({ proposedKey: name, currentKeys, prefix });
    } else {
      return createDedupedKey({
        proposedKey: `${join('_', initial(splitFieldName))}_${currentIndex + 1}`,
        currentKeys,
        prefix,
      });
    }
  } else {
    return proposedKey;
  }
};

type CreateComponentConfigProps = {
  itemConfig: ComponentSchemaValues['config'];
  fieldKeys: string[];
  step: InquiryTemplateVersionStepUi;
};

/**
 * Helper which creates a new component config and any
 * referenced fields onDrop in screen editor.
 *
 * Deduped field keys are generated for each fieldAttributeKey
 * in the component config, which are then used to create the
 * field(s) the component references.
 */
export const createComponentConfigAndFields = ({
  itemConfig,
  fieldKeys,
  step,
}: CreateComponentConfigProps): {
  componentConfig: UiComponentConfig;
  componentFields: FieldSchema[];
} => {
  let fieldConfig: Record<string, string> = {};
  let componentFields: FieldSchema[] = [];

  if (isUiFormComponentConfig(itemConfig as UiComponentConfig)) {
    fieldConfig = generateFieldKeysForUiFormComponent(
      itemConfig as UiFormComponentConfig,
      fieldKeys,
      step,
    );
    componentFields = Object.keys(fieldConfig).map(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (fieldAttributeKey: keyof InputMultiFieldComponentFieldConfig) =>
        createFieldSchema({
          config: itemConfig as UiFormComponentConfig,
          fieldAttributeKey: fieldAttributeKey,
          fieldKey: fieldConfig[fieldAttributeKey],
        }),
    );
  }

  const componentConfig = {
    ...itemConfig,
    name: generateRandomTypeName(getBaseName(itemConfig as UiComponentConfig), step),
    config: {
      ...itemConfig.config,
      ...fieldConfig,
    },
  } as UiComponentConfig;
  return { componentConfig, componentFields };
};

const generateFieldKeysForUiFormComponent = (
  config: UiFormComponentConfig,
  fieldKeys: string[],
  step: InquiryTemplateVersionStepUi,
): Record<string, string> => {
  const stepDisplayName = getNameForStep(step).toLowerCase();
  const fieldAttributeKeys = getComponentFieldKeys(config);
  return fieldAttributeKeys.reduce(
    (fieldKeysMap, fieldAttributeKey) => ({
      ...fieldKeysMap,
      [fieldAttributeKey]: createDedupedFieldKey(
        fieldKeys.concat(Object.values(fieldKeysMap)),
        snakeCase(config.type),
        stepDisplayName,
      ),
    }),
    {},
  );
};

export function addChildSizeToContainer({
  step,
  parentComponentPath,
  index,
  numComponents,
}: {
  step: InquiryTemplateVersionStepUi;
  parentComponentPath: string[];
  index: number;
  numComponents: number;
}) {
  const parentComponent = get(parentComponentPath, step.config.components);
  if (
    isStylableUiParentComponentConfig(parentComponent) &&
    parentComponent['style-variants'] != null
  ) {
    const updatedParentComponent = produce(parentComponent, (draftParentComponent) => {
      const numChildren = draftParentComponent.config.children.length;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draftParentComponent['style-variants'].map((styleVariant) => {
        let newChildSizes = [...styleVariant['child-sizes']];
        /**
         * Note (Evan): This should only happen with a new container component
         * We normalize the number of child-sizes entries to match the number of children
         */
        if (numChildren !== newChildSizes.length && newChildSizes.length === 0) {
          // default to equal child widths spanning the container width
          newChildSizes = new Array(numChildren).fill(1);
        } else {
          newChildSizes.splice(index, 0, ...new Array(numComponents).fill(1));
        }
        styleVariant['child-sizes'] = newChildSizes;
      });
    });
    return set(['config', 'components', ...parentComponentPath], updatedParentComponent, step);
  }

  return step;
}

export function removeChildSizeFromContainer({
  step,
  index,
  parentComponentPath,
}: {
  step: InquiryTemplateVersionStepUi;
  index: number;
  parentComponentPath: string[];
}) {
  const parentComponent = get(parentComponentPath, step.config.components);

  if (
    isStylableUiParentComponentConfig(parentComponent) &&
    parentComponent['style-variants'] != null
  ) {
    const updatedParentComponent = produce(parentComponent, (draftParentComponent) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      draftParentComponent['style-variants'].forEach((styleVariant) => {
        styleVariant['child-sizes'].splice(index, 1);
      });
    });
    return set(['config', 'components', ...parentComponentPath], updatedParentComponent, step);
  }

  return step;
}

export const removeComponentFromPath = (
  component: UiComponent | UiComponentConfig,
  step: InquiryTemplateVersionStepUi,
) => {
  let updatedStep = step;

  const componentPath =
    findComponentConfigPath(
      (componentConfig) => componentConfig.name === component.name,
      updatedStep.config.components,
    ) ?? [];

  const removalIndex = componentPath.length
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      parseInt(componentPath.at(-1))
    : updatedStep.config.components.length;

  let parentComponentChildrenPath: string[] = [];
  let parentPathComponents: UiComponentConfig[] = updatedStep.config.components;

  if (componentPath.length > 1) {
    parentComponentChildrenPath = componentPath.slice(0, -1);
    parentPathComponents = get(parentComponentChildrenPath, updatedStep.config.components);

    updatedStep = removeChildSizeFromContainer({
      step: updatedStep,
      parentComponentPath: parentComponentChildrenPath.slice(0, -2),
      index: removalIndex,
    });
  }

  return set(
    ['config', 'components', ...parentComponentChildrenPath],
    [
      ...parentPathComponents.slice(0, removalIndex),
      ...(parentPathComponents.at(removalIndex + 1) != null
        ? [...parentPathComponents.slice(removalIndex + 1)]
        : []),
    ],
    updatedStep,
  );
};

type AddComponentsAtIndexProps = {
  index: number;
  path?: string[] | null;
  configArray: UiComponentConfig[];
  componentConfigs: UiComponentConfig[];
  step: InquiryTemplateVersionStepUi;
};

export const addComponentsAtIndex = ({
  index,
  path,
  configArray,
  componentConfigs,
  step,
}: AddComponentsAtIndexProps) => {
  let updatedStep = step;
  const editPath = path != null ? ['config', 'components', ...path] : ['config', 'components'];

  updatedStep = set(
    editPath,
    [...configArray.slice(0, index), ...componentConfigs, ...configArray.slice(index)],
    updatedStep,
  );

  if (path != null) {
    updatedStep = addChildSizeToContainer({
      step: updatedStep,
      parentComponentPath: path.slice(0, -2),
      index,
      numComponents: componentConfigs.length,
    });
  }

  return updatedStep;
};

export const isComponentFromStep = (
  item: ScreenEditorComponentItem,
  step: InquiryTemplateVersionStepUi,
): boolean => {
  if (item.stepName == null) return true;
  return step.name === item.stepName;
};

type UnnestComponentsAtPathType = {
  component: UiParentComponentConfig;
  filterPredicate?: (componentConfig: UiComponentConfig) => boolean;
  step: InquiryTemplateVersionStepUi;
};

export const unnestComponentsAtPath = ({
  component,
  filterPredicate,
  step,
}: UnnestComponentsAtPathType): InquiryTemplateVersionStepUi => {
  const componentPath = findComponentConfigPath(
    (componentConfig) => componentConfig.name === component.name,
    step.config.components,
  );

  // if there is a predicate, we need to recursively search all nested components
  const nestedComponentConfigs = filterPredicate
    ? [...uiStepComponentConfigsRecursive(component.config.children)].filter(filterPredicate)
    : component.config.children;

  if (nestedComponentConfigs.length === 0) {
    return removeComponentFromPath(component, step);
  }

  const componentConfigArrayPath =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    componentPath.length > 1 ? componentPath.slice(0, -1) : componentPath;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const componentPathIndex = parseInt(componentPath.at(-1));

  const updatedStep = addComponentsAtIndex({
    index: componentPathIndex,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    path: componentConfigArrayPath.length > 1 ? componentConfigArrayPath : undefined,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    configArray:
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      componentConfigArrayPath.length > 1
        ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          get(componentConfigArrayPath, step.config.components)
        : step.config.components,
    componentConfigs: nestedComponentConfigs,
    step,
  });

  return removeComponentFromPath(component, updatedStep);
};

export const isShiftingComponentUp = (hoverPosition: DraggableHoverPosition) =>
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ['top', 'left'].includes(hoverPosition);

/**
 * @description Returns the index as an integer to where a component config should be placed
 * @param {number} originalComponentIndex The original index within the components/children array
 * of the component config
 * @param {number} hoverComponentIndex The index within the components/children array of the
 * component we want to use as the basis of insertion
 * @param {DraggableHoverPosition} hoverPosition The directional movement of where the original component config is
 * and where we want to move it to in relation to the hovered component
 * @returns {number}
 */
export const determineComponentShiftIndex = ({
  originalComponentIndex,
  hoverComponentIndex,
  hoverPosition,
}: {
  originalComponentIndex?: number | null;
  hoverComponentIndex: number;
  hoverPosition: DraggableHoverPosition;
}): number => {
  const isMovingComponentUp = isShiftingComponentUp(hoverPosition);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (originalComponentIndex > hoverComponentIndex || originalComponentIndex == null) {
    // if the component was below the hovered component, or we're inserting a new component, we want to move the
    // compoennt at the hoveredIndex or below it
    return isMovingComponentUp ? hoverComponentIndex : hoverComponentIndex + 1;
  } else {
    // the component was above the hovered component, so we either want to move it above, or at the hovered index
    return isMovingComponentUp ? hoverComponentIndex - 1 : hoverComponentIndex;
  }
};

export const restrictNestedComponentDrop = ({
  item,
  componentConfig,
}: {
  item: ScreenEditorComponentItem;
  componentConfig: UiComponentConfig;
}) => {
  // Note: We want components within clickable stack to be non-parent and non-interactive.
  // Nested ClickableStack and Buttons will maintain their own actions/transitions within ClickableStack
  // when we want the parent ClickableStack component to handle transition
  const restrictDropInClickableStack =
    [
      UiComponentConfigType.ClickableStack,
      UiComponentConfigType.HorizontalStack,
      UiComponentConfigType.ButtonCancel,
      UiComponentConfigType.ButtonComplete,
      UiComponentConfigType.ButtonSubmit,
      UiComponentConfigType.ButtonVerifyWithPersona,
    ].includes(item.type) && componentConfig.type === UiComponentConfigType.ClickableStack;

  return restrictDropInClickableStack;
};

export const getComponentDisplayName = (
  componentConfig: ScreenEditorComponentItem | UiComponentConfig,
) => {
  return ScreenEditorComponentConfigTypes[componentConfig.type].displayName;
};

export type MockInquiryPropsForUiComponent = {
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
  inquiryLoading: boolean;
};

export const createMockInquiryPropsForUiComponentEditor = (
  screen?: StepPreview,
  dispatch?: Dispatch,
): MockInquiryPropsForUiComponent => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const mockFunction = (): Promise<void> => undefined;
  return {
    inquiry: createMockInquiry(
      {
        'next-step': screen || createMockCombinedStep(UiStepType.GovernmentId),
        'redirect-uri': 'www.withpersona.com', // pass mock redirect-uri, so button complete is visible
      },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      null,
      'mock-inquiry',
    ),
    inquiryLoading: false,
    actionsApi: {
      handleLanguageChange: mockFunction,
      showMessage: mockFunction,
      setField: (field: string, value: unknown) => {
        dispatch?.(change(INQUIRY_TEMPLATE_SCREEN_EDITOR_FORM_NAME, field, value));
      },
      onSubmit: mockFunction,
      onActionButtonClick: mockFunction,
      onComplete: mockFunction,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onCancel: (): void => null,
      route: mockFunction,
      setComponent: mockFunction,
    },
  };
};

function getBIPATextandSubmitTriggerIndex(step: InquiryTemplateVersionStepUi) {
  // if this privacy policy text exists, we have a BIPA compliant screen
  const biometricStepPrivacyPolicyIndex = step.config.components.findIndex(
    (component) =>
      component.config != null &&
      'text-fallback-i18n-key' in component.config &&
      component.config['text-fallback-i18n-key'] ===
        'inquiry.ui.step_template.start.privacy_policy_biometric',
  );

  let directTransitionComponentAfterBIPAText = null;

  if (biometricStepPrivacyPolicyIndex === -1)
    return { bipaTextIndex: -1, directTransitionComponentAfterBIPAText };

  const transitionComponentsAfterBIPAText = step.config.components
    .slice(biometricStepPrivacyPolicyIndex + 1)
    .filter(isUiTransitionComponentConfig);

  if (transitionComponentsAfterBIPAText.length > 0) {
    const componentName = transitionComponentsAfterBIPAText[0].name;
    directTransitionComponentAfterBIPAText = {
      index: step.config.components.findIndex(
        (currComponent) => currComponent.name === componentName,
      ),
      name: componentName,
    };
  }

  return {
    bipaTextIndex: biometricStepPrivacyPolicyIndex,
    directTransitionComponentAfterBIPAText,
  };
}

type BIPAScreenMethodParams = {
  step: InquiryTemplateVersionStepUi;
  component: UiComponentConfig | undefined;
};

function isBIPAScreenWithComponent<
  T extends BIPAScreenMethodParams['step'],
  R extends BIPAScreenMethodParams['component'],
>(step: T, component: R): component is Extract<R, UiComponentConfig> {
  return isInitialStep(step) && component != null;
}

export function isCurrentComponentBIPASubmitTrigger({
  step,
  component,
}: BIPAScreenMethodParams): boolean {
  if (!isBIPAScreenWithComponent(step, component)) return false;
  const { directTransitionComponentAfterBIPAText } = getBIPATextandSubmitTriggerIndex(step);
  // check if the current component is the first trigger after the compliance text
  return directTransitionComponentAfterBIPAText != null
    ? directTransitionComponentAfterBIPAText.name === component.name
    : false;
}

export function isCurrentComponentBIPAText({ step, component }: BIPAScreenMethodParams): boolean {
  if (!isBIPAScreenWithComponent(step, component)) return false;
  const { bipaTextIndex } = getBIPATextandSubmitTriggerIndex(step);
  // check if the current component is the bipa text
  return bipaTextIndex > -1 ? step.config.components[bipaTextIndex].name === component.name : false;
}

export function isInsertingComponentBetweenBIPAComponents({
  step,
  index,
}: {
  step: InquiryTemplateVersionStepUi;
  index: number;
}) {
  if (!isInitialStep(step)) return false;
  const { bipaTextIndex, directTransitionComponentAfterBIPAText } =
    getBIPATextandSubmitTriggerIndex(step);
  if (bipaTextIndex === -1 || directTransitionComponentAfterBIPAText == null) return false;
  return index >= bipaTextIndex && index <= directTransitionComponentAfterBIPAText.index;
}

export function isTransitionComponentTargetSet(component: UiTransitionComponentConfig): boolean {
  return component.config['to-step'] != null || component.config['workflow-version-id'] != null;
}

export function cancelButtonEnabledForStep(step?: InquiryTemplateVersionStepUi) {
  return step?.config?.['hide-cancel-button'] !== true;
}

export function isHeaderComponentVisible(step?: InquiryTemplateVersionStepUi, navLogoUrl?: string) {
  return (
    step?.config?.['back-step'] != null || cancelButtonEnabledForStep(step) || navLogoUrl != null
  );
}

export function componentSelfServeUiConditions({
  componentConfig,
  isInitialStep,
  hasFreeTierAccess,
  isAccessibleForBasic,
}: {
  hasFreeTierAccess: boolean;
  componentConfig: UiComponentConfig;
  isInitialStep: boolean;
  isAccessibleForBasic: boolean;
}) {
  return {
    disableImageComponent: hasFreeTierAccess
      ? isImageComponentConfig(componentConfig) && !isAccessibleForBasic && !isInitialStep
      : true,
  };
}

export function getStepComponentConfigs(step: InquiryTemplateVersionStepUi) {
  return step.config.components.reduce(
    (componentsObject, component) => {
      if (![UiComponentConfigType.Footer].includes(component.type)) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        componentsObject.nonFooterComponentsConfig.push(component);
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        componentsObject.footerComponentConfig.push(component);
      }

      return componentsObject;
    },
    { nonFooterComponentsConfig: [], footerComponentConfig: [] },
  );
}

export function stepContainsFooter(step: InquiryTemplateVersionStepUi) {
  return step.config.components.some(isUiFooterComponentConfig);
}

export function configureComponentForPaste(
  fieldKeys: string[],
  step: InquiryTemplateVersionStepUi,
  component: UiComponentConfig,
): UiComponentConfig {
  const { configuredComponent } = configureComponentForPasteRecursive(fieldKeys, step, component);
  return configuredComponent;
}

function configureComponentForPasteRecursive(
  fieldKeys: string[],
  step: InquiryTemplateVersionStepUi,
  component: UiComponentConfig,
): { configuredComponent: UiComponentConfig; additionalKeys: string[] } {
  const additionalKeys: string[] = [];
  const configuredComponent = produce(component, (draftComponent) => {
    if (isUiTransitionComponentConfig(draftComponent)) {
      draftComponent.config = clearedTransitionConfig(draftComponent.config);
    }

    if (isUiFormComponentConfig(draftComponent)) {
      const fieldConfig = generateFieldKeysForUiFormComponent(draftComponent, fieldKeys, step);
      // replace the field keys with new deduped keys
      draftComponent.config = {
        ...draftComponent.config,
        ...fieldConfig,
      };

      additionalKeys.push(...Object.values(fieldConfig));
    }

    // recursively configure children for paste
    if (isUiParentComponentConfig(draftComponent)) {
      draftComponent.config.children = draftComponent.config.children.map((child) => {
        // pass in all the keys generated so far
        const { configuredComponent, additionalKeys: childAdditionalKeys } =
          configureComponentForPasteRecursive(fieldKeys.concat(additionalKeys), step, child);

        // add the additonal generated keys from the child
        additionalKeys.push(...childAdditionalKeys);
        return configuredComponent;
      });
      draftComponent.name = generateRandomTypeName(
        getBaseName(component),
        step,
        draftComponent.config.children,
      );
    } else {
      draftComponent.name = generateRandomTypeName(getBaseName(component), step);
    }
  });
  return { configuredComponent, additionalKeys };
}

export function configureFieldSchemaForPaste(
  fieldSchema: FieldSchema[],
  component: UiComponentConfig,
): FieldSchema[] {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  let additionalFields = [];
  if (isUiParentComponentConfig(component)) {
    // recursively extract field schema from children
    additionalFields = component.config.children.reduce((fieldSchemaAccumulator, child) => {
      return fieldSchemaAccumulator.concat(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        configureFieldSchemaForPaste(fieldSchema.concat(fieldSchemaAccumulator), child),
      );
    }, []);
  }
  if (isUiFormComponentConfig(component)) {
    const fieldAttributeKeys = getComponentFieldKeys(component);
    const existingFieldKeys = fieldSchema.map((field) => field.key);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    additionalFields = fieldAttributeKeys.reduce(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (fieldSchemaAccumulator, fieldAttributeKey: keyof InputMultiFieldComponentFieldConfig) => {
        const fieldKey = (component as UiFormMultiFieldComponentConfig).config[fieldAttributeKey];
        if (!existingFieldKeys.includes(fieldKey)) {
          let fieldSchema = createFieldSchema({
            config: component,
            fieldAttributeKey,
            fieldKey,
          });

          // Update the newly created field schema with the corresponding options from the component
          if (
            isUiFormFieldOptionComponentConfig(component) &&
            isFieldSchemaWithChoices(fieldSchema)
          ) {
            const componentOptions = component.config.options.map((option) => option.value);
            fieldSchema = produce(fieldSchema, (draftFieldSchema) => {
              draftFieldSchema.config.options = componentOptions;
            });
          }
          fieldSchemaAccumulator.push(fieldSchema);
        }
        return fieldSchemaAccumulator;
      },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      additionalFields,
    );
  }

  return additionalFields;
}

function getComponentSelectedPasteIndex(
  step: InquiryTemplateVersionStepUi,
  configArray: UiComponentConfig[],
  configArrayPath: string[],
  selectedComponent: UiComponentConfig,
): number {
  if (isUiParentComponentConfig(selectedComponent)) return configArray.length;

  // paste below the selected component, or below the BIPA Transition component if pasting between BIPA components
  let pasteIndex =
    configArray.findIndex((currComponent) => currComponent.name === selectedComponent.name) + 1;
  if (
    isEmpty(configArrayPath) &&
    isInsertingComponentBetweenBIPAComponents({ step, index: pasteIndex })
  ) {
    const { directTransitionComponentAfterBIPAText } = getBIPATextandSubmitTriggerIndex(step);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    pasteIndex = directTransitionComponentAfterBIPAText.index + 1;
  }
  return pasteIndex;
}

function getScreenSelectedPasteIndex(step: InquiryTemplateVersionStepUi): number {
  const { nonFooterComponentsConfig } = getStepComponentConfigs(step);
  const insertionIndex = nonFooterComponentsConfig.length;
  return insertionIndex;
}

export function getPasteIndex(
  step: InquiryTemplateVersionStepUi,
  configArray: UiComponentConfig[],
  configArrayPath: string[],
  selectedComponent?: UiComponentConfig,
): number {
  return selectedComponent == null
    ? getScreenSelectedPasteIndex(step)
    : getComponentSelectedPasteIndex(step, configArray, configArrayPath, selectedComponent);
}

/**
 * Given a selected component, it either returns the path to the component's children if it's a parent component,
 * or the path to the component's parent if it's a child component. Additionally, the function also returns the index
 * at which the component should be pasted.
 * @param step - the step to find the component path in
 * @param copiedComponent - the component that is being pasted
 * @param selectedComponent - the component to find the path to
 * @returns {string[], UiComponentConfig[], number} - the path to the component and the component array
 * @type {(InquiryTemplateVersionStepUi, UiComponentConfig, UiComponentConfig) =>
 * {configArrayPath: string[], configArray: UiComponentConfig[], pasteIndex: number}}
 **/
export function getPasteInsertionConfigs(
  step: InquiryTemplateVersionStepUi,
  copiedComponent: UiComponentConfig,
  selectedComponent?: UiComponentConfig,
): { configArray: UiComponentConfig[]; configArrayPath: string[]; pasteIndex: number } {
  // if no component is selected or the selected component is a footer, return the step's components
  if (selectedComponent == null || isUiFooterComponentConfig(copiedComponent)) {
    return {
      configArrayPath: [],
      configArray: step.config.components,
      pasteIndex: getPasteIndex(step, step.config.components, []),
    };
  }

  let configArrayPath = findComponentConfigPath(
    (component: UiComponentConfig) => component.name === selectedComponent.name,
    step.config.components,
  );

  configArrayPath = isUiParentComponentConfig(selectedComponent)
    ? // component is a parent component, find path to it's own config array
      // the path returns the parent component so we need to append ['config', 'children'] to get it's own config array
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [...configArrayPath, 'config', 'children']
    : // component is a child component, find path to its parent's config array
      // the path returns the child component so we need to remove the last element to get the parent's config array
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [...configArrayPath].slice(0, -1);

  const configArray = isEmpty(configArrayPath)
    ? step.config.components
    : get(configArrayPath, step.config.components);

  return {
    configArrayPath,
    configArray,
    pasteIndex: getPasteIndex(step, configArray, configArrayPath, selectedComponent),
  };
}

// TODO: rip and replace with logic to support copy and paste of image hosted components
//       once assets live on the theme set level.
export function containsImageHostedComponent(component: UiComponentConfig) {
  if (isUiParentComponentConfig(component)) {
    return component.config.children.some(containsImageHostedComponent);
  }
  return isImageHostedComponentConfig(component);
}

export function configureComponentForCopy(
  step: InquiryTemplateVersionStepUi,
  componentConfig: UiComponentConfig,
  clientData: StepUi,
): UiComponentConfig {
  return produce(componentConfig, (draftComponentConfig) => {
    const componentPreview = uiStepAllComponents(clientData).find((componentConfig) => {
      return componentConfig.name === draftComponentConfig.name;
    });
    // set default translations if component has i18n key
    //
    const editorComponentSchema = EDITOR_COMPONENTS_SCHEMA[componentConfig.type];
    if (
      isTextComponentConfig(draftComponentConfig) &&
      isTextComponent(componentPreview) &&
      editorComponentSchema
    ) {
      const updatedComponentText = getUpdatedComponentText(
        draftComponentConfig as UiTextComponentConfig,
        componentPreview,
        editorComponentSchema as EditorComponentSchema<UiTextComponentConfig>,
        'en-US',
      );

      draftComponentConfig = setTranslationDefaults(
        draftComponentConfig as UiTextComponentConfig,
        updatedComponentText,
      );
    }

    // recursively configure children for copy
    if (isUiParentComponentConfig(draftComponentConfig)) {
      draftComponentConfig.config.children = draftComponentConfig.config.children.map((child) => {
        return configureComponentForCopy(step, child, clientData);
      });
    }
  });
}

export function duplicateTransitionComponentTargetExists(
  originalComponent: UiComponentConfig,
  screen: InquiryTemplateVersionStepUi,
): boolean {
  if (!isUiTransitionComponentConfig(originalComponent)) return false;
  const transitionComponents = uiStepAllComponentConfigs(screen).filter(
    isUiTransitionComponentConfig,
  );

  return transitionComponents.some(
    (comparedComponent) =>
      comparedComponent.name !== originalComponent.name &&
      transitionComponentTargetsAreEqual(originalComponent, comparedComponent),
  );
}

function transitionComponentTargetsAreEqual(
  originalComponentConfig: UiTransitionComponentConfig,
  comparedComponentConfig: UiTransitionComponentConfig,
): boolean {
  const transitionComponentAttributeKeys: (keyof TransitionConfigAttributes)[] = [
    'to-step',
    'workflow-version-id',
  ];

  return isMatch(
    pick(transitionComponentAttributeKeys, originalComponentConfig.config),
    pick(transitionComponentAttributeKeys, comparedComponentConfig.config),
  );
}

export const COMPONENT_CANNOT_BE_COPIED_ERROR_MESSAGE = 'This component cannot be copied.';

export function copyComponentErrorMessage(
  component: UiComponentConfig | undefined,
): string | undefined {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (RESTRICTED_COMPONENTS.includes(component?.type)) {
    return COMPONENT_CANNOT_BE_COPIED_ERROR_MESSAGE;
  }
}

export const SCREEN_IS_PRECONFIGURED_FLOW_ERROR_MESSAGE =
  'This screen is part of a preconfigured flow. You cannot add components to this screen.';
export const SCREEN_CONTAINS_FOOTER_ERROR_MESSAGE = 'Screen already contains a footer component.';
export const CANNOT_PASTE_CUSTOM_ASSETS_ERROR_MESSAGE = `Cannot paste image components containing custom assets from a different inquiry template.`;
export const cannotPasteWithinComponentErrorMessage = (
  toPasteComponentName: string,
  destinationComponentName: string,
) => {
  return `Cannot paste ${toPasteComponentName} component within ${destinationComponentName}`;
};
export const noAccessToComponentErrorMessage = (requiredAccessTier: string) => {
  return `You do not have access to this component. \
          This component is available on the ${requiredAccessTier} plan.`;
};

export function pasteComponentErrorMessage({
  step,
  selectedComponent,
  copiedComponentData,
  inquiryTemplateVersionId,
  hasAccessTier,
}: {
  step: InquiryTemplateVersionStepUi | undefined;
  selectedComponent: UiComponentConfig | undefined;
  inquiryTemplateVersionId: string;
  copiedComponentData: ComponentCopyData;
  hasAccessTier: (accessTier: EditorAccessTier | undefined) => boolean;
}): string | undefined {
  if (!hasAccessTier(ComponentAccessTiers[copiedComponentData.component?.type])) {
    const requiredAccessTier = getProductUpsellLevel(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ComponentAccessTiers[copiedComponentData.component?.type],
    );
    return noAccessToComponentErrorMessage(requiredAccessTier);
  } else if (step == null) {
    return SCREEN_IS_PRECONFIGURED_FLOW_ERROR_MESSAGE;
  } else if (isUiFooterComponentConfig(copiedComponentData.component) && stepContainsFooter(step)) {
    return SCREEN_CONTAINS_FOOTER_ERROR_MESSAGE;
  } else if (
    inquiryTemplateVersionId !== copiedComponentData.inquiryTemplateVersionId &&
    containsImageHostedComponent(copiedComponentData.component)
  ) {
    // Note: Temporary solution to prevent copying of image hosted components
    // https://github.com/persona-id/persona-web/issues/34783
    return CANNOT_PASTE_CUSTOM_ASSETS_ERROR_MESSAGE;
  } else if (
    selectedComponent != null &&
    restrictNestedComponentDrop({
      item: copiedComponentData.component as ScreenEditorComponentItem,
      componentConfig: selectedComponent,
    })
  ) {
    const toPasteComponentName = getComponentDisplayName(copiedComponentData.component);
    const destinationComponentName = getComponentDisplayName(selectedComponent);
    return cannotPasteWithinComponentErrorMessage(toPasteComponentName, destinationComponentName);
  }
}
